import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { industries, services } from '../data/mockData';
import { Button } from '../components/ui/button';
import { ChevronRight, Check, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';

const Industries = () => {
  const { industryId } = useParams();

  // If viewing a specific industry
  if (industryId) {
    const industry = industries.find(i => i.id === industryId);
    
    if (!industry) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-[#1e3a52] mb-4">Industry Not Found</h1>
            <Link to="/industries">
              <Button className="bg-[#5ba4c8] hover:bg-[#4a93b7]">
                View All Industries
              </Button>
            </Link>
          </div>
        </div>
      );
    }

    return (
      <div>
        <SEO 
          title={`${industry.title} Security Services - Professional Security Solutions`}
          description={`${industry.description} Customized security solutions for ${industry.title.toLowerCase()} in Miami, Florida. Professional security guards and surveillance services.`}
          keywords={`${industry.title.toLowerCase()} security, security for ${industry.title.toLowerCase()}, ${industry.title.toLowerCase()} security Miami, ${industry.title.toLowerCase()} security Florida, commercial security services`}
          image={industry.image}
        />
        {/* Hero Section */}
        <section className="relative h-[400px] flex items-center">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('${industry.image}')` }}
          />
          <div className="absolute inset-0 bg-[#1e3a52]/80" />
          
          <div className="relative z-10 max-w-7xl mx-auto px-4 w-full">
            <div className="flex items-center gap-2 text-sm text-gray-300 mb-4">
              <Link to="/" className="hover:text-[#5ba4c8]">Home</Link>
              <ChevronRight size={16} />
              <Link to="/industries" className="hover:text-[#5ba4c8]">Industries</Link>
              <ChevronRight size={16} />
              <span className="text-[#5ba4c8]">{industry.title}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white uppercase tracking-wider">
              {industry.title}
            </h1>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-3xl font-bold text-[#1e3a52] mb-6">
                  Security Solutions for {industry.title}
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {industry.description}
                </p>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Our experienced security professionals understand the unique challenges faced by {industry.title.toLowerCase()}. We provide customized security solutions that address specific risks and requirements while maintaining a welcoming environment for staff and visitors.
                </p>
                
                <h3 className="text-xl font-bold text-[#1e3a52] mb-4">Our Security Services Include:</h3>
                <ul className="space-y-3 mb-8">
                  {services.slice(0, 4).map((service, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <div className="w-6 h-6 bg-[#5ba4c8] rounded-full flex items-center justify-center">
                        <Check size={14} className="text-white" />
                      </div>
                      <span className="text-gray-700">{service.title}</span>
                    </li>
                  ))}
                </ul>
                
                <Link to="/quote">
                  <Button className="bg-[#5ba4c8] hover:bg-[#4a93b7] text-white px-8 py-3">
                    Request a Quote <ArrowRight size={16} className="ml-2" />
                  </Button>
                </Link>
              </div>
              
              <div>
                <img 
                  src={industry.image} 
                  alt={industry.title}
                  className="w-full h-[400px] object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Other Industries */}
        <section className="py-16 bg-gray-100">
          <div className="max-w-7xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-[#1e3a52] mb-12">Other Industries We Serve</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {industries.filter(i => i.id !== industryId).slice(0, 3).map((ind) => (
                <Link 
                  key={ind.id} 
                  to={`/industries/${ind.id}`}
                  className="group relative h-[200px] overflow-hidden rounded-lg"
                >
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                    style={{ backgroundImage: `url('${ind.image}')` }}
                  />
                  <div className="absolute inset-0 bg-[#1e3a52]/60 group-hover:bg-[#1e3a52]/70 transition-colors" />
                  
                  <div className="relative h-full flex items-center justify-center">
                    <h3 className="text-white text-xl font-bold text-center">{ind.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Industries overview page
  return (
    <div>
      <SEO 
        title="Industries We Serve - Security Services for Multiple Industries"
        description="Professional security services for apartment complexes, medical facilities, banks, grocery stores, hotels, construction sites, and more in Miami, Florida."
        keywords="commercial security services, apartment security, medical facility security, bank security, retail security, hotel security, construction site security, industry security solutions Miami"
      />
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&h=800&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-[#1e3a52]/80" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 w-full">
          <div className="flex items-center gap-2 text-sm text-gray-300 mb-4">
            <Link to="/" className="hover:text-[#5ba4c8]">Home</Link>
            <ChevronRight size={16} />
            <span className="text-[#5ba4c8]">Industries</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white uppercase tracking-wider">
            Industries Serviced
          </h1>
          <p className="text-gray-300 mt-4 max-w-2xl">
            Professional security solutions for diverse industries across Florida
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry) => (
              <Link 
                key={industry.id} 
                to={`/industries/${industry.id}`}
                className="group relative h-[250px] overflow-hidden rounded-lg shadow-md"
              >
                <div 
                  className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundImage: `url('${industry.image}')` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1e3a52] via-[#1e3a52]/50 to-transparent group-hover:from-[#1e3a52]/90 transition-colors" />
                
                <div className="relative h-full flex flex-col justify-end p-6">
                  <h3 className="text-white text-xl font-bold mb-2">
                    {industry.title}
                  </h3>
                  <p className="text-gray-300 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    {industry.description}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#5ba4c8]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Don't See Your Industry?
          </h2>
          <p className="text-white/80 mb-8">
            We provide custom security solutions for all types of businesses. Contact us to discuss your needs.
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[#1e3a52] hover:bg-gray-100 px-8 py-3">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Industries;
